import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, ToggleButton, ToggleButtonGroup, Select, MenuItem,
  FormControl, InputLabel, IconButton, CircularProgress, Alert, Pagination as MuiPagination, Tooltip,
  Checkbox, Menu, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { listImages, uploadImages, deleteImage, deleteAllImages, getImageThumbnail, verifyAllImages, exportImagesToProject } from '../../api/annotatorImages';
import { getProject, listProjects } from '../../api/annotatorProjects';
import type { AnnotatorProject, AnnotatorImageItem } from '../../types/annotator';

type Filter = 'all' | 'annotated' | 'unannotated' | 'unverified';
const PAGE_SIZE = 60;

function ThumbnailImage({ projectId, imageId, alt }: { projectId: number; imageId: number; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    getImageThumbnail(projectId, imageId)
      .then((u) => { url = u; setSrc(u); })
      .catch(console.error);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [projectId, imageId]);
  if (!src) return <Box sx={{ width: '100%', aspectRatio: '1', bgcolor: 'action.hover', borderRadius: 1 }} />;
  return <img src={src} alt={alt} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4 }} />;
}

export default function AnnotatorAnnotationListPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  const initialPage = Number(searchParams.get('page') ?? 1);
  const initialLabelId = Number(searchParams.get('labelId') ?? '');

  const [project, setProject] = useState<AnnotatorProject | null>(null);
  const [images, setImages] = useState<AnnotatorImageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<Filter>(
    initialFilter === 'annotated' || initialFilter === 'unannotated' || initialFilter === 'unverified' ? initialFilter as Filter : 'all'
  );
  const [uploading, setUploading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [labelId, setLabelId] = useState<number | undefined>(
    Number.isFinite(initialLabelId) && initialLabelId > 0 ? initialLabelId : undefined
  );
  const fileRef = useRef<HTMLInputElement>(null);

  // Multiselect + right-click "export unannotated images to project"
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [allProjects, setAllProjects] = useState<AnnotatorProject[]>([]);
  const [exportDestProjectId, setExportDestProjectId] = useState<number | ''>('');
  const [exportRemoveSrc, setExportRemoveSrc] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportResult, setExportResult] = useState<{ exported_count: number; skipped_count: number } | null>(null);

  function toggleSelect(imageId: number, e: React.MouseEvent | React.ChangeEvent) {
    e.preventDefault();
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId); else next.add(imageId);
      return next;
    });
  }

  function handleContextMenu(imageId: number, e: React.MouseEvent) {
    e.preventDefault();
    setSelected((prev) => (prev.has(imageId) ? prev : new Set([imageId])));
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
  }

  function openExportDialog() {
    setContextMenu(null);
    setExportDestProjectId('');
    setExportRemoveSrc(true);
    setExportError('');
    setExportResult(null);
    setExportDialogOpen(true);
    if (allProjects.length === 0) {
      listProjects().then(setAllProjects).catch(() => {});
    }
  }

  async function handleExport() {
    if (!projectId || exportDestProjectId === '' || selected.size === 0) return;
    setExporting(true);
    setExportError('');
    try {
      const result = await exportImagesToProject(
        Number(projectId),
        Array.from(selected),
        exportDestProjectId as number,
        exportRemoveSrc,
      );
      setExportResult(result);
      setSelected(new Set());
      if (exportRemoveSrc) setReloadKey((k) => k + 1);
    } catch (err: any) {
      setExportError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  const editorSearchParams = new URLSearchParams({ filter, page: String(page), pageSize: String(PAGE_SIZE) });
  if (labelId !== undefined) editorSearchParams.set('labelId', String(labelId));

  useEffect(() => {
    if (!projectId) return;
    getProject(Number(projectId)).then(setProject).catch(console.error);
  }, [projectId]);

  useEffect(() => {
    const nextParams: Record<string, string> = { filter, page: String(page), pageSize: String(PAGE_SIZE) };
    if (labelId !== undefined) nextParams.labelId = String(labelId);
    setSearchParams(nextParams, { replace: true });
  }, [filter, page, labelId, setSearchParams]);

  useEffect(() => {
    if (!projectId) return;
    const annotated = filter === 'annotated' ? true : filter === 'unannotated' ? false : undefined;
    const isVerified = filter === 'unverified' ? false : undefined;
    listImages(Number(projectId), annotated, page, PAGE_SIZE, labelId, isVerified)
      .then((result) => { setImages(result.items); setTotal(result.total); setPages(result.pages); })
      .catch(console.error);
  }, [projectId, filter, page, reloadKey, labelId]);

  async function handleFiles(files: FileList | null) {
    if (!files || !projectId) return;
    setUploading(true);
    try {
      await uploadImages(Number(projectId), Array.from(files));
      setFilter('all');
      setLabelId(undefined);
      setPage(1);
      setReloadKey((k) => k + 1);
    } finally { setUploading(false); }
  }

  async function handleDeleteAll() {
    if (!projectId) return;
    const filterLabel = filter === 'unverified' ? 'unverified ' : filter === 'annotated' ? 'annotated ' : filter === 'unannotated' ? 'unannotated ' : '';
    if (!confirm(`Delete ALL ${total.toLocaleString()} ${filterLabel}image(s)? This cannot be undone.`)) return;
    setDeletingAll(true);
    try {
      const isVerified = filter === 'unverified' ? false : undefined;
      await deleteAllImages(Number(projectId), isVerified);
      setPage(1);
      setReloadKey((k) => k + 1);
    } finally { setDeletingAll(false); }
  }

  async function handleVerifyAll() {
    if (!projectId) return;
    setVerifyingAll(true);
    try {
      const result = await verifyAllImages(Number(projectId));
      setReloadKey((k) => k + 1);
    } finally { setVerifyingAll(false); }
  }

  async function handleDelete(img: AnnotatorImageItem, e: React.MouseEvent) {
    e.preventDefault();
    if (!confirm(`Delete "${img.original_filename}"?`)) return;
    await deleteImage(Number(projectId), img.id);
    setReloadKey((k) => k + 1);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }

  return (
    <Box onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Button component={RouterLink} to={`/annotator/projects/${projectId}`} size="small" sx={{ mb: 0.5 }}>
            ← Back to Project
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Images{total > 0 && <Typography component="span" variant="body2" color="text.secondary"> ({total.toLocaleString()})</Typography>}
          </Typography>
          {selected.size > 0 && (
            <Typography variant="body2" color="primary">
              {selected.size} selected — right-click to export, or{' '}
              <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setSelected(new Set())}>
                clear
              </Box>
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => { if (v) { setFilter(v); setPage(1); } }}
            size="small"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="annotated">Annotated</ToggleButton>
            <ToggleButton value="unannotated">Unannotated</ToggleButton>
            <ToggleButton value="unverified" sx={{ color: 'warning.main' }}>Unverified</ToggleButton>
          </ToggleButtonGroup>

          {project && project.labels.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Label</InputLabel>
              <Select
                value={labelId ?? ''}
                label="Label"
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setLabelId(Number.isFinite(v) && v > 0 ? v : undefined);
                  setPage(1);
                }}
              >
                <MenuItem value="">All labels</MenuItem>
                {project.labels.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <Button variant="contained" startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />} onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          {filter === 'unverified' && total > 0 && (
            <Button variant="outlined" color="warning" startIcon={verifyingAll ? <CircularProgress size={16} /> : <CheckCircleIcon />} onClick={handleVerifyAll} disabled={verifyingAll}>
              {verifyingAll ? 'Verifying…' : 'Accept All'}
            </Button>
          )}
          {total > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteAll} disabled={deletingAll}>
              {deletingAll ? 'Deleting…' : 'Delete All'}
            </Button>
          )}
          <input ref={fileRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files)} />
        </Box>
      </Box>

      {total === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No images yet.</Typography>
          <Typography variant="body2" color="text.secondary">Drag & drop images here or click Upload.</Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
            {images.map((img) => (
              <Paper
                key={img.id}
                onContextMenu={(e) => handleContextMenu(img.id, e)}
                sx={{
                  position: 'relative', overflow: 'hidden', '&:hover .thumb-actions': { opacity: 1 },
                  outline: selected.has(img.id) ? '2px solid' : 'none', outlineColor: 'primary.main',
                }}
              >
                <Checkbox
                  size="small"
                  checked={selected.has(img.id)}
                  onClick={(e) => toggleSelect(img.id, e)}
                  sx={{
                    position: 'absolute', top: 2, left: 2, zIndex: 1, p: 0.5,
                    bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '50%',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                  }}
                />
                <Box
                  component={RouterLink}
                  to={`/annotator/projects/${projectId}/images/${img.id}?${editorSearchParams.toString()}`}
                  state={{ imageIds: images.map((i) => i.id), page, pageSize: PAGE_SIZE, filter, labelId, pages }}
                  sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                  <ThumbnailImage projectId={Number(projectId)} imageId={img.id} alt={img.original_filename} />
                  <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                    {!img.is_verified
                      ? <Tooltip title="AI generated – unverified"><WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} /></Tooltip>
                      : img.is_annotated
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    }
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, py: 0.25 }}>
                  <Typography variant="caption" noWrap title={img.original_filename} sx={{ flex: 1 }}>{img.original_filename}</Typography>
                  <IconButton size="small" onClick={(e) => handleDelete(img, e)} sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>

          {pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <MuiPagination count={pages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
            </Box>
          )}
        </>
      )}

      <Menu
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={openExportDialog}>
          <DriveFileMoveIcon fontSize="small" sx={{ mr: 1 }} />
          Export {selected.size} unannotated image{selected.size !== 1 ? 's' : ''} to project…
        </MenuItem>
      </Menu>

      <Dialog open={exportDialogOpen} onClose={() => !exporting && setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export {selected.size} Image{selected.size !== 1 ? 's' : ''} to Another Project</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Only images with no annotations will be exported — any selected images that already have
              labels are skipped automatically.
            </Typography>
            {exportError && <Alert severity="error">{exportError}</Alert>}
            {exportResult && (
              <Alert severity="success">
                Exported {exportResult.exported_count} image{exportResult.exported_count !== 1 ? 's' : ''}.
                {exportResult.skipped_count > 0 && ` Skipped ${exportResult.skipped_count} (already annotated).`}
              </Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Destination Project</InputLabel>
              <Select
                value={exportDestProjectId}
                label="Destination Project"
                onChange={(e) => setExportDestProjectId(e.target.value as number)}
                disabled={exporting || !!exportResult}
              >
                {allProjects
                  .filter((p) => p.id !== Number(projectId))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportRemoveSrc}
                  onChange={(e) => setExportRemoveSrc(e.target.checked)}
                  disabled={exporting || !!exportResult}
                />
              }
              label="Remove from this project after exporting"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>Close</Button>
          {!exportResult && (
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={exporting || exportDestProjectId === '' || selected.size === 0}
              startIcon={exporting ? <CircularProgress size={16} /> : <DriveFileMoveIcon />}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
